import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight, BookOpen, CalendarCheck, CheckCircle2, Flame, Lightbulb, Loader2, Lock,
  Sparkles, Star, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, toApiError } from '@/lib/api';
import {
  CHALLENGE_POINTS, challengeKeys, type TodayResponse, type TodaysChallenge,
} from '@/lib/challengeQueries';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ANSWERING TODAY'S SPARK
 *
 * Two columns: the question on the left, why it is worth answering on the
 * right.
 *
 * The three steps — Question, Explanation, Takeaway — are locked until the
 * answer is in, and that is the whole shape of the thing. They are not
 * decoration: they are the reason to answer rather than skip, and they are
 * why a WRONG answer still keeps the streak. You get the teaching either way.
 *
 * No timer. A countdown turns a moment of reflection into a reflex test, and
 * it is the part most likely to interrupt somebody who is actually thinking.
 * ═══════════════════════════════════════════════════════════════════════════
 */

interface Verdict {
  isCorrect: boolean;
  correctOptionId: string | null;
  explanation: string | null;
  takeaway: string | null;
  streak: { current: number };
  pointsEarned: number;
}

type Step = 'question' | 'explanation' | 'takeaway';

export function ChallengeDialog({
  challenge,
  open,
  onClose,
  streak,
  stats,
}: {
  challenge: TodaysChallenge;
  open: boolean;
  onClose: () => void;
  streak: TodayResponse['streak'];
  stats: TodayResponse['stats'];
}) {
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<string | null>(
    challenge.myAnswer?.selectedOptionId ?? null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [usedHint, setUsedHint] = useState(false);
  const [step, setStep] = useState<Step>('question');

  /**
   * Seeded from the server when they have already answered, so re-opening
   * shows the same verdict rather than an empty form they cannot fill.
   */
  const [verdict, setVerdict] = useState<Verdict | null>(
    challenge.myAnswer
      ? {
          isCorrect: challenge.myAnswer.isCorrect,
          correctOptionId: challenge.myAnswer.correctOptionId,
          explanation: challenge.explanation,
          takeaway: challenge.takeaway,
          streak: { current: streak.current },
          pointsEarned: challenge.myAnswer.isCorrect ? CHALLENGE_POINTS : 0,
        }
      : null,
  );

  const done = verdict !== null;

  async function showHint() {
    setUsedHint(true);
    try {
      const { data } = await api.post<{ hint: string | null }>(`/challenges/${challenge.id}/hint`);
      setHint(data.hint ?? 'No hint for this one.');
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function submit() {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post<Verdict>(`/challenges/${challenge.id}/answer`, {
        optionId: selected,
        usedHint,
      });
      setVerdict(data);
      // Straight to the teaching — it is what they just earned.
      setStep(data.explanation ? 'explanation' : 'takeaway');
      await queryClient.invalidateQueries({ queryKey: challengeKeys.today() });
      await queryClient.invalidateQueries({ queryKey: ['achievements'] });
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto p-0 sm:max-w-4xl">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 border-b px-5 py-3.5 pr-12">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-brand-400/15 dark:text-brand-200">
            <Lightbulb className="h-4 w-4" aria-hidden />
          </span>
          <DialogTitle className="text-base">Today’s Learning Spark</DialogTitle>

          <span className="rounded-md border px-2.5 py-1 text-[12px] text-muted-foreground">
            Topic: {challenge.topic}
          </span>

          {streak.current > 0 && (
            // Filled, matching the card it was opened from.
            <span className="ml-auto flex items-center gap-1.5 rounded-full bg-warning px-3 py-1 text-xs font-semibold text-warning-foreground">
              <Flame className="h-3.5 w-3.5" aria-hidden />
              {streak.current} day streak
            </span>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr_300px]">
          {/* ── The question ───────────────────────────────────────────── */}
          <div className="min-w-0 border-b p-5 lg:border-b-0 lg:border-r">
            <Steps step={step} onStep={setStep} unlocked={done} verdict={verdict} />

            {step === 'question' && (
              <>
                <p className="mt-5 text-[17px] font-semibold leading-snug">
                  {challenge.question}
                </p>
                {!done && (
                  <p className="mt-1.5 text-[13px] text-muted-foreground">
                    Take a moment to think about it.
                  </p>
                )}

                <div
                  className="mt-4 space-y-2"
                  role="radiogroup"
                  aria-label={challenge.question}
                >
                  {challenge.options.map((option, index) => {
                    const chosen = selected === option.id;
                    const isTheAnswer = verdict?.correctOptionId === option.id;
                    const gotItWrong = done && chosen && !verdict?.isCorrect;

                    return (
                      <label
                        key={option.id}
                        className={cn(
                          'flex items-center gap-3 rounded-xl border p-3.5 transition-colors',
                          done ? 'cursor-default' : 'cursor-pointer hover:bg-muted/40',
                          chosen && !done && 'border-primary bg-accent',
                          isTheAnswer && 'border-success/50 bg-success/10',
                          gotItWrong && 'border-destructive/50 bg-destructive/10',
                        )}
                      >
                        <input
                          type="radio"
                          name={challenge.id}
                          className="sr-only"
                          checked={chosen}
                          disabled={done}
                          onChange={() => setSelected(option.id)}
                        />
                        <span
                          className={cn(
                            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                            chosen && !done && 'border-primary bg-primary text-primary-foreground',
                            isTheAnswer && 'border-success bg-success text-white',
                            gotItWrong && 'border-destructive bg-destructive text-white',
                          )}
                          aria-hidden
                        >
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="flex-1 text-sm">{option.text}</span>
                        {isTheAnswer && (
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
                        )}
                        {gotItWrong && (
                          <XCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
                        )}
                      </label>
                    );
                  })}
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                  {/*
                    A true statement rather than the reassuring one.

                    The mock said "your answer is safe and anonymous". It is
                    not anonymous — the answer is stored against the person and
                    the author sees the pass rate — so printing that would be a
                    privacy promise the system does not keep.
                  */}
                  <p className="text-[12px] text-muted-foreground">
                    {done
                      ? 'Answered. Come back tomorrow for the next one.'
                      : 'One answer a day. Right or wrong, it keeps your streak.'}
                  </p>

                  {done ? (
                    <Button
                      variant="outline"
                      onClick={() => setStep(verdict?.explanation ? 'explanation' : 'takeaway')}
                    >
                      See why
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button onClick={() => void submit()} disabled={!selected || submitting}>
                      {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      Submit answer
                    </Button>
                  )}
                </div>
              </>
            )}

            {step === 'explanation' && (
              <Teaching
                title={verdict?.isCorrect ? 'You got it right' : 'Here’s why'}
                body={verdict?.explanation}
                empty="No explanation was written for this one."
                onNext={() => setStep('takeaway')}
                nextLabel="Takeaway"
              />
            )}

            {step === 'takeaway' && (
              <Teaching
                title="The takeaway"
                body={verdict?.takeaway}
                empty="No takeaway was written for this one."
                onNext={onClose}
                nextLabel="Done"
              />
            )}
          </div>

          {/* ── Progress and the reasons ───────────────────────────────── */}
          <aside className="space-y-3 bg-muted/20 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Your progress
            </p>

            <div className="grid grid-cols-3 gap-2">
              <Stat
                icon={Star}
                value={stats.totalPoints.toLocaleString()}
                label="Total points"
                tone="text-warning"
              />
              <Stat
                icon={Flame}
                value={String(streak.current)}
                label="Day streak"
                tone="text-warning"
              />
              <Stat
                icon={CalendarCheck}
                value={`${stats.answered}/${stats.available}`}
                label="Answered"
                tone="text-brand-700"
              />
            </div>

            {/* The verdict, or what is on offer. Never both, and never the
                advertised figure after a wrong answer. */}
            {done ? (
              <div
                className={cn(
                  'rounded-xl border p-3',
                  verdict!.isCorrect
                    ? 'border-success/30 bg-success/10'
                    : 'border-border bg-background',
                )}
              >
                <p className="flex items-center gap-2 text-sm font-semibold">
                  {verdict!.isCorrect ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
                      Correct · +{verdict!.pointsEarned} points
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-muted-foreground" aria-hidden />
                      Not this time
                    </>
                  )}
                </p>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  {verdict!.isCorrect
                    ? 'Your streak is alive — see you tomorrow.'
                    : 'Your streak is safe. Answering is what keeps it going.'}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-3 dark:border-brand-700 dark:bg-brand-400/10">
                <p className="flex items-center gap-2 text-sm font-semibold text-brand-800">
                  <Star className="h-4 w-4 fill-warning text-warning" aria-hidden />
                  +{CHALLENGE_POINTS} points
                </p>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  For a correct answer. Answering at all keeps your streak.
                </p>
              </div>
            )}

            {/* Free, and only while it can still help. */}
            {!done && challenge.hasHint && (
              <div className="rounded-xl border bg-background p-3">
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <Sparkles className="h-4 w-4 text-brand-700" aria-hidden />
                  Need a hint?
                </p>
                {hint ? (
                  <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
                    {hint}
                  </p>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => void showHint()}
                  >
                    Show hint
                  </Button>
                )}
              </div>
            )}

            {/* Chosen by the author, never inferred — a wrong recommendation
                here is worse than none. */}
            {challenge.course && (
              <div className="rounded-xl border bg-background p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Related to your learning
                </p>
                <p className="mt-1.5 flex items-start gap-2 text-sm font-medium leading-snug">
                  <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" aria-hidden />
                  {challenge.course.title}
                </p>
                <Button asChild variant="ghost" size="sm" className="mt-1.5 w-full">
                  <Link to={`/courses/${challenge.course.id}`} onClick={onClose}>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Question → Explanation → Takeaway.
 *
 * The last two are locked until the answer is in. Shown locked rather than
 * hidden, because knowing there is something on the other side is most of the
 * reason to answer instead of closing the dialog.
 */
function Steps({
  step,
  onStep,
  unlocked,
  verdict,
}: {
  step: Step;
  onStep: (next: Step) => void;
  unlocked: boolean;
  verdict: Verdict | null;
}) {
  const steps: { key: Step; label: string }[] = [
    { key: 'question', label: 'Question' },
    { key: 'explanation', label: 'Explanation' },
    { key: 'takeaway', label: 'Takeaway' },
  ];

  return (
    <div className="flex items-center gap-1">
      {steps.map(({ key, label }, index) => {
        const locked = key !== 'question' && !unlocked;
        const active = step === key;
        const answeredCorrectly = key === 'question' && verdict?.isCorrect;

        return (
          <button
            key={key}
            type="button"
            disabled={locked}
            onClick={() => onStep(key)}
            aria-current={active}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-colors',
              active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
              locked ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted',
            )}
          >
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold',
                active ? 'bg-brand-800 text-white' : 'bg-muted text-muted-foreground',
              )}
              aria-hidden
            >
              {locked ? <Lock className="h-2.5 w-2.5" /> : answeredCorrectly ? '✓' : index + 1}
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Teaching({
  title,
  body,
  empty,
  onNext,
  nextLabel,
}: {
  title: string;
  body: string | null | undefined;
  empty: string;
  onNext: () => void;
  nextLabel: string;
}) {
  return (
    <div className="mt-5">
      <h3 className="text-[17px] font-semibold">{title}</h3>
      <p
        className={cn(
          'mt-2 text-sm leading-relaxed',
          body ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {body || empty}
      </p>

      <div className="mt-5 flex justify-end border-t pt-4">
        <Button onClick={onNext}>
          {nextLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  value,
  label,
  tone,
}: {
  icon: typeof Star;
  value: string;
  label: string;
  tone: string;
}) {
  return (
    <div className="rounded-xl border bg-background px-2 py-2.5 text-center">
      <Icon className={cn('mx-auto h-4 w-4', tone)} aria-hidden />
      <p className="mt-1 text-[15px] font-semibold tabular-nums">{value}</p>
      <p className="text-[10.5px] leading-tight text-muted-foreground">{label}</p>
    </div>
  );
}
